import { useForm } from 'react-hook-form'
import { useCallback, useEffect } from 'react'
import moment from 'moment/moment'
import {
  type PartnerPickRecurringShiftsRecurringShift,
  usePartnerPickRecurringShiftCreateMutation,
  usePartnerPickRecurringShiftDeleteMutation,
  usePartnerPickRecurringShiftUpdateMutation,
} from 'domains/partner-pick-devices/api'
import { trackEvent } from 'utils/events/trackEvent'
import { type ShiftType } from 'domains/shifts/components/types'
import {
  mapPartnerPickRecurringShiftFormDataToCreateMutationPayload,
  mapPartnerPickRecurringShiftFormDataToUpdateMutationPayload,
} from './partnerPickRecurringShiftForm.utils'
import { type PartnerPickRecurringShiftsFormData } from './recurringShift.types'

interface UsePartnerPickRecurringShiftFormProps {
  formData: Partial<PartnerPickRecurringShiftsFormData>
  warehouseLocationId: string
  shiftId?: number
  recurringShiftId?: number
  shiftType: ShiftType
  shopperId: number
  onAddNonRecurringShift: (shopperId: number, start: Date, end: Date, onCall: boolean) => void
  date: string
  onShiftSave: (
    SavedShift: PartnerPickRecurringShiftsRecurringShift,
    shopperId: number,
    shiftId: number,
    conflictIds: string[]
  ) => void
  onApiError: (error: any) => void
  setErrorMessage: (error: string) => void
  devicePrioritizationEnabled: boolean
}

export const usePartnerPickRecurringShiftsForm = ({
  formData,
  warehouseLocationId,
  shiftId,
  recurringShiftId,
  shiftType,
  shopperId,
  date,
}: UsePartnerPickRecurringShiftFormProps) => {
  const {
    watch,
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    errors,
    formState,
    unregister,
  } = useForm<PartnerPickRecurringShiftsFormData>({
    defaultValues: formData,
    mode: 'onChange',
  })

  const [
    submitCreate,
    {
      data: recurringShiftCreateData,
      loading: recurringShiftCreateLoading,
      error: recurringShiftCreateError,
    },
  ] = usePartnerPickRecurringShiftCreateMutation()

  const [
    submitUpdate,
    {
      data: recurringShiftUpdateData,
      loading: recurringShiftUpdateLoading,
      error: recurringShiftUpdateError,
    },
  ] = usePartnerPickRecurringShiftUpdateMutation()

  const [
    submitDelete,
    {
      data: recurringShiftDeleteData,
      loading: recurringShiftDeleteLoading,
      error: recurringShiftDeleteError,
    },
  ] = usePartnerPickRecurringShiftDeleteMutation()

  useEffect(() => {
    register({ name: 'repeatShift' })
    register({ name: 'daysOfWeek' })
    register({ name: 'recurEndDate' })
    register({ name: 'shiftAllocationType' })
  }, [register])

  const submitForm = useCallback(
    (formData: PartnerPickRecurringShiftsFormData) => {
      const isNonRecurringShiftBeingConvertedToRecurring =
        !!shiftId && !recurringShiftId && formData.daysOfWeek?.length > 0
      const recurStartDate = moment(date)

      const isEditMode = !!recurringShiftId
      if (!isEditMode || isNonRecurringShiftBeingConvertedToRecurring) {
        const variables = mapPartnerPickRecurringShiftFormDataToCreateMutationPayload(
          formData,
          warehouseLocationId,
          shiftType,
          shopperId,
          recurStartDate
        )
        trackEvent({
          id: 'shifts.shift_management.enhanced_recurring_shifts.recurring_shift_added',
          description: 'Enhanced Recurring Shift created ',
          data: {
            shiftId,
            retailerLocationId: warehouseLocationId,
            variables,
          },
        })
        submitCreate({
          variables,
        })
      } else {
        const variables = mapPartnerPickRecurringShiftFormDataToUpdateMutationPayload(
          formData,
          warehouseLocationId,
          recurringShiftId,
          shiftType,
          shopperId,
          recurStartDate
        )
        trackEvent({
          id: 'shifts.shift_management.enhanced_recurring_shifts.recurring_shift_edited',
          description: 'Enhanced Recurring Shift edited ',
          data: {
            shiftId,
            retailerLocationId: warehouseLocationId,
            variables,
          },
        })
        submitUpdate({
          variables,
        })
      }
    },
    [
      warehouseLocationId,
      submitCreate,
      submitUpdate,
      shiftType,
      shopperId,
      date,
      recurringShiftId,
      shiftId,
    ]
  )

  const deleteRecurringShift = useCallback(
    (recurringShiftId: any, date: string) => {
      const variables = {
        id: recurringShiftId,
        deleteShiftsFromDate: moment(date).toISOString(),
        shiftType,
      }

      trackEvent({
        id: 'shifts.shift_management.enhanced_recurring_shifts.recurring_shift_deleted',
        description: 'Enhanced Recurring Shift deleted ',
        data: {
          variables,
        },
      })
      submitDelete({
        variables,
      })
    },
    [submitDelete, shiftType]
  )

  return {
    watch,
    control,
    setValue,
    register,
    unregister,
    handleSubmit,
    errors,
    submitForm,
    getValues,
    formState,
    deleteRecurringShift,
    formSubmitLoading: recurringShiftCreateLoading || recurringShiftUpdateLoading,
    formSubmitData:
      recurringShiftCreateData?.partnerPickRecurringShiftCreate?.partnerPickRecurringShift ||
      recurringShiftUpdateData?.partnerPickRecurringShiftUpdate?.partnerPickRecurringShift,
    formSubmitSuccess: Boolean(recurringShiftCreateData || recurringShiftUpdateData),
    formSubmitError: recurringShiftCreateError || recurringShiftUpdateError,
    shiftDeleteLoading: recurringShiftDeleteLoading,
    shiftDeleteSuccess: Boolean(recurringShiftDeleteData),
    shiftDeleteError: recurringShiftDeleteError,
  }
}
